export interface SelectDropdownArgs {
  selector?: string;
  value?: string;
  text?: string;
  withinSelector?: string;
  timeout?: number;
}

export interface SelectDropdownResult {
  success: boolean;
  message: string;
}

/**
 * Select an option in a dropdown/select element.
 * This tool can find options by value or text content.
 */
export async function selectDropdown(args: SelectDropdownArgs): Promise<SelectDropdownResult> {
  const timeout = args.timeout || 5000;
  const startTime = Date.now();

  if (!args.selector) {
    throw new Error("selector is required for selectDropdown");
  }

  if (!args.value && !args.text) {
    throw new Error("Either value or text must be provided for selectDropdown");
  }

  // Get the container to search within
  const getContainer = (): HTMLElement | null => {
    if (args.withinSelector) {
      const container = document.querySelector(args.withinSelector);
      if (container && container instanceof HTMLElement) {
        return container;
      }
    }
    return document.body;
  };

  // Find the select element
  const findElement = (): HTMLSelectElement | null => {
    const container = getContainer();
    if (!container) return null;

    const element = container.querySelector(args.selector!);
    if (element && element instanceof HTMLSelectElement) {
      return element;
    }

    return null;
  };

  // Wait for element to appear
  while (Date.now() - startTime < timeout) {
    const select = findElement();
    if (select) {
      // Find the option to select
      let option: HTMLOptionElement | null = null;

      if (args.value) {
        // Try to find by value
        option = Array.from(select.options).find((opt) => opt.value === args.value) || null;
      }

      if (!option && args.text) {
        // Try to find by text content
        option =
          Array.from(select.options).find((opt) => opt.textContent?.trim() === args.text?.trim()) || null;
      }

      if (option) {
        // Set selected
        select.selectedIndex = option.index;
        select.value = option.value;

        // Trigger change event
        select.dispatchEvent(new Event("change", { bubbles: true }));

        return {
          success: true,
          message: `Successfully selected option "${option.textContent?.trim() || option.value}" in dropdown with selector "${args.selector}"`,
        };
      } else {
        throw new Error(
          `Option not found${args.value ? ` with value "${args.value}"` : ""}${args.text ? ` with text "${args.text}"` : ""} in dropdown with selector "${args.selector}"`,
        );
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  throw new Error(`Select element not found after ${timeout}ms (looking for selector "${args.selector}")`);
}
